// ============================================================
// Производное состояние страницы «Тарифы» (общая логика desktop + mobile).
//
// Страница отвечает на пять вопросов: какой тариф → до какой даты → сколько
// сегодня использовано → что будет дальше → что можно сделать. Demo бесплатно
// и без срока (с 2026-10-22): оплаченный период закончился — ученик снова на
// Demo. Предоплаты СЛЕДУЮЩЕГО периода нет (убрана 2026-07-02), поэтому действий
// всего два — купить период с сейчас (Demo) и апгрейд Pro→Max внутри текущего
// периода; флаги payable/canUpgrade бэкенда — источник правды.
// ============================================================
import type { BillingOverview, PlanTier } from '../api/planApi';

export type PaidTier = Extract<PlanTier, 'PRO' | 'PREMIUM'>;

// ---- Герой «Мой доступ» --------------------------------------------------

export type HeroState =
  /** Demo: бесплатно и без срока, дневные жетоны тарифа и несгораемые — в quota. */
  | { kind: 'demo' }
  /** Оплаченный период идёт (Pro/Max). Закончился — снова Demo (продления нет). */
  | { kind: 'paid'; plan: PaidTier; startsAt: string; endsAt: string; daysLeft: number; totalDays: number };

export function getHeroState(o: BillingOverview): HeroState {
  const c = o.current;
  if (!c.paid || !c.endsAt) return { kind: 'demo' };
  const plan = c.plan as PaidTier;
  return { kind: 'paid', plan, startsAt: c.startsAt, endsAt: c.endsAt, daysLeft: c.daysLeft ?? 0, totalDays: o.periodDays };
}

/**
 * Прошедшая доля периода [startsAt, endsAt), 0–100: полоска срока пустая в
 * день оплаты и заполняется к окончанию. Считаем по времени, а не по целым
 * дням: daysLeft с бэка округлён вверх и первые сутки держал бы 0%.
 */
export function periodElapsedPct(startsAt: string, endsAt: string, now: number = Date.now()): number {
  const start = Date.parse(startsAt);
  const total = Date.parse(endsAt) - start;
  if (!(total > 0)) return 0;
  return Math.round(Math.min(100, Math.max(0, ((now - start) / total) * 100)));
}

// ---- Действие карточки тарифа -------------------------------------------
// Описывает ТОЛЬКО блок цены/CTA. Бейдж «Активна» — отдельно (isCurrentPlan).

export type CardAction =
  /** Demo: покупка периода — стартует с момента оплаты. */
  | { kind: 'buyCurrent'; tier: PaidTier; amount: number }
  /** Pro→Max сейчас: доплата за остаток текущего периода. */
  | { kind: 'upgradeCurrent'; amount: number; full: number; until: string; daysLeft: number }
  /** Pro недоступен, пока действует Max (даунгрейд внутри периода запрещён). */
  | { kind: 'locked' }
  /** Действий нет (Demo-карточка; активный тариф и т.п.). */
  | { kind: 'none' };

export function getCardAction(tier: PlanTier, o: BillingOverview): CardAction {
  if (tier === 'DEMO') return { kind: 'none' };
  const t = tier as PaidTier;
  const c = o.current;
  const price = t === 'PRO' ? o.prices.proRub : o.prices.premiumRub;

  // Платного периода нет (Demo) → купить период с сейчас.
  if (c.payable) return { kind: 'buyCurrent', tier: t, amount: price };
  if (!c.paid) return { kind: 'none' }; // не должно достигаться (payable ↔ !paid)

  if (t === 'PREMIUM' && c.canUpgrade && c.upgradePay != null && c.endsAt) {
    return { kind: 'upgradeCurrent', amount: c.upgradePay, full: o.prices.premiumRub, until: c.endsAt, daysLeft: c.daysLeft ?? 0 };
  }
  if (t === 'PRO' && c.plan === 'PREMIUM') return { kind: 'locked' };
  return { kind: 'none' }; // свой активный тариф / Max при активном Max
}

/** Этот тариф — активный оплаченный план пользователя (для ленты «Ваш тариф»). */
export function isCurrentPlan(tier: PlanTier, o: BillingOverview): boolean {
  return o.current.paid && o.current.plan === tier;
}
